import { type ShowcaseGridDimensions, squareDimensions } from "@/lib/showcase-grid/dimensions";
import type { ContentOrderWithGames } from "@/types/content-order";
import type { ShowcaseGridDisplayItem } from "@/types/showcase-grid";

export type GameSectionLayout = "row" | "grid" | "curved_row";
export type GameSectionCurveDirection = "up" | "down";
export type GameSectionIconSize = 1 | 2 | 3;

export function sectionTileOrderKey(
  games: Array<{ id: number; game_id: string; display_order: number }>,
  assets: Array<{ id: number; content_type: string; asset_url: string; display_order: number }> = []
): string {
  return sectionTilesInOrder(games, assets)
    .map((tile) =>
      tile.kind === "game"
        ? `${tile.order}:g:${tile.id}:${tile.gameId}`
        : `${tile.order}:a:${tile.id}:${tile.contentType}:${tile.assetUrl}`
    )
    .join(",");
}

export function sectionTileIndex(
  games: Array<{ id: number; game_id: string; display_order: number }>,
  assets: Array<{ id: number; display_order: number }>,
  match: (tile: { kind: "game" | "asset"; id: number; gameId?: string }) => boolean
): number {
  return sectionTilesInOrder(games, assets).findIndex(match);
}

function sectionTilesInOrder(
  games: Array<{ id: number; game_id: string; display_order: number }>,
  assets: Array<{ id: number; content_type?: string; asset_url?: string; display_order: number }>
) {
  return [
    ...games.map((game) => ({
      kind: "game" as const,
      id: game.id,
      gameId: game.game_id,
      order: game.display_order,
    })),
    ...assets.map((asset) => ({
      kind: "asset" as const,
      id: asset.id,
      contentType: asset.content_type,
      assetUrl: asset.asset_url,
      order: asset.display_order,
    })),
  ].sort((left, right) => left.order - right.order || left.id - right.id);
}

export function readGameSectionLayout(value: unknown): GameSectionLayout {
  if (value === "grid") return "grid";
  if (value === "curved_row") return "curved_row";
  return "row";
}

export function readGameSectionCurveDirection(value: unknown): GameSectionCurveDirection {
  return value === "down" ? "down" : "up";
}

export function readGameSectionEnlargeMiddle(value: unknown): boolean {
  return value !== false;
}

export function readGameSectionShowHeader(value: unknown): boolean {
  return value !== false;
}

/** Sparse grid centering. Defaults to on; only applies when layout is grid. */
export function readGameSectionCenterWhenSparse(value: unknown): boolean {
  return value !== false;
}

/** Equal left/right inset for grid and curved-row sections, in columns of a 6-column frame. */
export const SECTION_SIDE_PADDINGS = [0, 0.5, 1, 1.5, 2] as const;
export type SectionSidePadding = (typeof SECTION_SIDE_PADDINGS)[number];
export const SECTION_SIDE_PADDING_FRAME = 6;

export function readGameSectionSidePadding(value: unknown): SectionSidePadding {
  const numeric = typeof value === "number" ? value : Number(value);
  return (SECTION_SIDE_PADDINGS as readonly number[]).includes(numeric)
    ? (numeric as SectionSidePadding)
    : 0;
}

/**
 * Each 0.5 of side padding removes one column (0.5 on the left and 0.5 on the right)
 * and leaves the rest for the section.
 */
export function sidePaddingContentColumns(padding: number): number {
  return SECTION_SIDE_PADDING_FRAME - readGameSectionSidePadding(padding) * 2;
}

/** Side padding applies from the desktop curved-row breakpoint up, not on phones. */
export const SECTION_SIDE_PADDING_DESKTOP_MEDIA = "(min-width: 640px)" as const;

export function readGameSectionIconSize(value: unknown): GameSectionIconSize {
  if (value === 2 || value === 3) return value;
  if (value === "2" || value === "3") return Number(value) as GameSectionIconSize;
  return 1;
}

export type SectionSlot = ShowcaseGridDimensions;

export const DEFAULT_SECTION_SLOT: SectionSlot = { cols: 1, rows: 1 };

export type SectionDragPhase = "idle" | "dragging" | "dropped";

/**
 * `move` shifts tiles and each keeps its own size. `swap` (Shift) exchanges two tiles and
 * leaves every slot size where it is.
 */
export type SectionDropMode = "move" | "swap";

export interface SectionDragState {
  phase: SectionDragPhase;
  activeId: string | null;
  overId: string | null;
  /** Slot index the dragged tile would occupy if released now. */
  overSlot: number | null;
  /** Shift exchanges only the dragged tile and the tile in `overSlot`. */
  swap: boolean;
}

export function idleSectionDrag(): SectionDragState {
  return { phase: "idle", activeId: null, overId: null, overSlot: null, swap: false };
}

export function trackSectionDrag(
  state: SectionDragState,
  event:
    | { type: "start"; id: string; slot: number; swap?: boolean }
    | { type: "over"; id: string; slot: number }
    | { type: "swap"; swap: boolean }
    | { type: "end" }
    | { type: "cancel" }
): SectionDragState {
  if (event.type === "start") {
    return {
      phase: "dragging",
      activeId: event.id,
      overId: event.id,
      overSlot: event.slot,
      swap: event.swap ?? false,
    };
  }
  if (event.type === "over") {
    if (state.phase !== "dragging") return state;
    if (state.overId === event.id && state.overSlot === event.slot) return state;
    return { ...state, overId: event.id, overSlot: event.slot };
  }
  if (event.type === "swap") {
    if (state.phase !== "dragging" || state.swap === event.swap) return state;
    return { ...state, swap: event.swap };
  }
  if (event.type === "end") {
    if (state.phase !== "dragging") return state;
    return {
      phase: "dropped",
      activeId: state.activeId,
      overId: state.overId,
      overSlot: state.overSlot,
      swap: state.swap,
    };
  }
  return idleSectionDrag();
}

export function readSectionSlot(value: unknown): SectionSlot {
  if (!value || typeof value !== "object") return DEFAULT_SECTION_SLOT;
  const record = value as { cols?: unknown; rows?: unknown };
  return {
    cols: readGameSectionIconSize(record.cols),
    rows: readGameSectionIconSize(record.rows),
  };
}

export function readSectionSlots(value: unknown): SectionSlot[] {
  if (!Array.isArray(value)) return [];
  return value.map((slot) => readSectionSlot(slot));
}

type SectionGameRef = { id: number; game_id: string; display_order: number };
type SectionAssetRef = { id: number; display_order: number };
type SectionTileMatch = (tile: { kind: "game" | "asset"; id: number; gameId?: string }) => boolean;

/**
 * Slots after the matching tiles leave the section. Each removed tile's slot
 * goes with it, so the tiles after it keep the sizes they had.
 */
export function slotsAfterRemovingTiles(
  slots: SectionSlot[],
  games: SectionGameRef[],
  assets: SectionAssetRef[],
  matches: SectionTileMatch[]
): SectionSlot[] {
  const removedIndexes = matches
    .map((match) => sectionTileIndex(games, assets, match))
    .filter((index) => index >= 0)
    .sort((left, right) => right - left);
  const next = alignSectionSlots(slots, games.length + assets.length);
  for (const index of removedIndexes) next.splice(index, 1);
  return next;
}

export function slotsAfterRemovingGame(
  slots: SectionSlot[],
  games: SectionGameRef[],
  assets: SectionAssetRef[],
  gameId: string
): SectionSlot[] {
  return slotsAfterRemovingTiles(slots, games, assets, [
    (tile) => tile.kind === "game" && tile.gameId === gameId,
  ]);
}

export function slotsAfterRemovingAsset(
  slots: SectionSlot[],
  games: SectionGameRef[],
  assets: SectionAssetRef[],
  assetId: number
): SectionSlot[] {
  return slotsAfterRemovingTiles(slots, games, assets, [
    (tile) => tile.kind === "asset" && tile.id === assetId,
  ]);
}

/** New tiles go on the end as 1×1. */
export function slotsAfterAddingTiles(
  slots: SectionSlot[],
  currentCount: number,
  addedCount: number
): SectionSlot[] {
  return alignSectionSlots(alignSectionSlots(slots, currentCount), currentCount + addedCount);
}

/**
 * Slots after the game picker closes: deselected games take their slots with
 * them, newly selected games get 1×1 slots at the end.
 */
export function slotsAfterSelectingGames(
  slots: SectionSlot[],
  games: SectionGameRef[],
  assets: SectionAssetRef[],
  selectedGameIds: string[]
): SectionSlot[] {
  const selected = new Set(selectedGameIds);
  const removed = games.filter((game) => !selected.has(game.game_id));
  const kept = slotsAfterRemovingTiles(
    slots,
    games,
    assets,
    removed.map(
      (game): SectionTileMatch =>
        (tile) =>
          tile.kind === "game" && tile.gameId === game.game_id
    )
  );
  const added = selectedGameIds.filter(
    (gameId) => !games.some((game) => game.game_id === gameId)
  ).length;
  return slotsAfterAddingTiles(kept, kept.length, added);
}

export type PlacedSectionTile<G, A> =
  | { kind: "game"; index: number; slot: SectionSlot; game: G }
  | { kind: "asset"; index: number; slot: SectionSlot; asset: A };

/** Games and assets in display order, each paired with the slot at its position. */
export function placeSectionTiles<
  G extends { id: number; display_order: number },
  A extends { id: number; display_order: number },
>(games: readonly G[], assets: readonly A[], slots: SectionSlot[]): PlacedSectionTile<G, A>[] {
  const ordered = [
    ...games.map((game) => ({ kind: "game" as const, order: game.display_order, game })),
    ...assets.map((asset) => ({ kind: "asset" as const, order: asset.display_order, asset })),
  ].sort((left, right) => left.order - right.order);
  const aligned = alignSectionSlots(slots, ordered.length);
  return ordered.map((entry, index) =>
    entry.kind === "game"
      ? { kind: "game", index, slot: aligned[index], game: entry.game }
      : { kind: "asset", index, slot: aligned[index], asset: entry.asset }
  );
}

/** Length matches the items. Extra slots are dropped from the end. Missing slots are 1×1. */
export function alignSectionSlots(slots: SectionSlot[], count: number): SectionSlot[] {
  const next = slots.slice(0, Math.max(0, count)).map((slot) => ({ ...slot }));
  while (next.length < count) next.push({ ...DEFAULT_SECTION_SLOT });
  return next;
}

export function setSectionSlot(
  slots: SectionSlot[],
  index: number,
  next: SectionSlot,
  count: number
): SectionSlot[] {
  const aligned = alignSectionSlots(slots, count);
  if (index < 0 || index >= aligned.length) return aligned;
  aligned[index] = {
    cols: readGameSectionIconSize(next.cols),
    rows: readGameSectionIconSize(next.rows),
  };
  return aligned;
}

/** Order the row would have if the dragged id were released into `overSlot`. */
export function previewSectionOrder(
  ids: readonly string[],
  activeId: string | null,
  overSlot: number | null,
  swap = false
): string[] {
  if (!activeId || overSlot == null) return [...ids];
  const fromIndex = ids.indexOf(activeId);
  if (swap) return swapSectionItems(ids, fromIndex, overSlot);
  return reorderSectionItems(ids, fromIndex, overSlot);
}

/** Slot the pointer is over in the current preview. Ignores the landing outline. */
export function sectionSlotUnderPointer<T>(
  placement: readonly SectionDragPlacement<T>[],
  overId: string,
  idOf: (item: T) => string
): number | null {
  const hit = placement.find((entry) => idOf(entry.item) === overId && !entry.isDropSlot);
  return hit ? hit.slotIndex : null;
}

/**
 * Pointer hits win. A pointer that is not inside a tile keeps the last slot,
 * because the landing outline is not a target and the nearest tile would
 * flip the preview on every move. Keyboard drags have no pointer, so they
 * use the nearest tile.
 */
export function sectionCollisionHits<T>(
  pointerHits: readonly T[],
  centerHits: readonly T[],
  hasPointer: boolean
): readonly T[] {
  if (pointerHits.length > 0) return pointerHits;
  if (!hasPointer) return centerHits;
  return [];
}

export interface SectionDragPlacement<T> {
  item: T;
  /** Index whose slot size and grid cell this tile occupies. Slots stay put. */
  slotIndex: number;
  isDropSlot: boolean;
}

/** Tiles in drop order, each sitting in the slot it would occupy on release. */
export function placeSectionDragPreview<T>(
  items: readonly T[],
  activeId: string | null,
  overSlot: number | null,
  idOf: (item: T) => string,
  swap = false
): SectionDragPlacement<T>[] {
  const order = previewSectionOrder(items.map(idOf), activeId, overSlot, swap);
  const byId = new Map(items.map((item) => [idOf(item), item]));
  return order.flatMap((id, slotIndex) => {
    const item = byId.get(id);
    if (!item) return [];
    return [{ item, slotIndex, isDropSlot: activeId != null && id === activeId }];
  });
}

export function reorderSectionItems<T>(
  items: readonly T[],
  fromIndex: number,
  toIndex: number
): T[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

/** Exchange two positions and leave every other item where it is. */
export function swapSectionItems<T>(items: readonly T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return [...items];
  }
  const next = [...items];
  const displaced = next[toIndex];
  next[toIndex] = next[fromIndex];
  next[fromIndex] = displaced;
  return next;
}

/** Grid tiles for one section. Size comes from the slot, not the item. */
export function sectionToShowcaseItems(order: ContentOrderWithGames): ShowcaseGridDisplayItem[] {
  const elements = [...(order.elements ?? [])].sort((a, b) => a.display_order - b.display_order);
  const items: ShowcaseGridDisplayItem[] = [];

  const slots = readSectionSlots(order.grid_slots);
  for (const element of elements) {
    const dims = slots[element.display_order] ?? DEFAULT_SECTION_SLOT;
    if (element.content_type === "game" && element.game) {
      items.push({
        id: `section-game-${element.id}`,
        type: "game",
        position: element.display_order,
        gridCols: dims.cols,
        gridRows: dims.rows,
        game: element.game,
      });
      continue;
    }
    if (
      (element.content_type === "image" || element.content_type === "rive") &&
      element.asset_url
    ) {
      items.push({
        id: `section-asset-${element.id}`,
        type: element.content_type,
        position: element.display_order,
        gridCols: dims.cols,
        gridRows: dims.rows,
        assetUrl: element.asset_url,
      });
    }
  }

  if (items.length > 0) return items;

  return order.games.map((item) => {
    const dims = squareDimensions(1);
    return {
      id: `section-game-${item.id}`,
      type: "game" as const,
      position: item.display_order,
      gridCols: dims.cols,
      gridRows: dims.rows,
      game: item.game,
    };
  });
}

export function isGameSectionAssetType(value: string): value is "image" | "rive" {
  return value === "image" || value === "rive";
}

export interface AdminSectionItem {
  id: number;
  game_id: string | null;
  display_order: number;
  content_type?: string | null;
  asset_url?: string | null;
  game: unknown;
}

export function splitAdminSectionItems(items: AdminSectionItem[], rowId: string) {
  const sorted = [...items].sort((a, b) => a.display_order - b.display_order);
  const games = sorted
    .filter((item) => item.game && item.game_id)
    .map((item) => ({
      id: item.id,
      row_id: rowId,
      game_id: item.game_id as string,
      display_order: item.display_order,
      content_type: "game" as const,
      game: item.game,
    }));
  const assets = sorted.flatMap((item) => {
    if (!isGameSectionAssetType(item.content_type ?? "") || !item.asset_url) return [];
    return [
      {
        id: item.id,
        row_id: rowId,
        content_type: item.content_type as "image" | "rive",
        asset_url: item.asset_url,
        display_order: item.display_order,
      },
    ];
  });
  return { games, assets };
}
